const TEST_DATA_CAMEL_KEY_RE = /^testData(\d*)$/;
const TEST_DATA_KEBAB_KEY_RE = /^test-data(\d*)$/;

export const STEP_PLACEHOLDER_TOKEN_REGEX =
  /(\$\{[\w-]+\}|#\{uiIdentifier\}|@\{attribute\})/g;

export function extractPlaceholderKey(token: string): string {
  if (token.startsWith("${") && token.endsWith("}")) {
    return token.slice(2, -1);
  }
  if (token.startsWith("#{") && token.endsWith("}")) {
    return token.slice(2, -1);
  }
  if (token.startsWith("@{") && token.endsWith("}")) {
    return token.slice(2, -1);
  }
  return token;
}

export function normalizeTestDataKey(key: string): string {
  if (!key) return key;
  if (key === "test-data") return "testData";
  if (key === "ui-identifier") return "uiIdentifier";
  if (key === "test-data-profile") return "testDataProfile";
  if (TEST_DATA_KEBAB_KEY_RE.test(key)) {
    const suffix = key.replace("test-data", "");
    return `testData${suffix}`;
  }
  return key;
}

export function toLegacyTestDataKey(key: string): string {
  if (!key) return key;
  if (key === "testData") return "test-data";
  if (key === "testDataProfile") return "test-data-profile";
  if (TEST_DATA_CAMEL_KEY_RE.test(key)) {
    const suffix = key.replace("testData", "");
    return `test-data${suffix}`;
  }
  return key;
}

function valueFromEntry(entry: any): string | null {
  if (entry == null) return null;
  if (typeof entry === "string") return entry;
  if (typeof entry === "number" || typeof entry === "boolean") {
    return String(entry);
  }
  if (typeof entry === "object" && "value" in entry) {
    const value = (entry as any).value;
    if (value == null) return null;
    return String(value);
  }
  return null;
}

export function resolveTestDataValue(
  step: any,
  placeholderKey: string,
  fallback = "",
): string {
  const normalizedKey = normalizeTestDataKey(placeholderKey);
  const legacyKey = toLegacyTestDataKey(normalizedKey);
  const testDataMap =
    step?.dataMap?.testData ??
    step?.dataMap?.["test-data"] ??
    step?.data?.testData ??
    step?.data?.["test-data"] ??
    {};

  const rawValue =
    testDataMap?.[normalizedKey] ??
    testDataMap?.[legacyKey] ??
    testDataMap?.[placeholderKey];
  const resolved = valueFromEntry(rawValue);
  return resolved == null || resolved === "" ? fallback : resolved;
}

export function buildReplacementMapFromStep(
  step: any,
  fixed: Record<string, string> = {},
): Record<string, string> {
  const out: Record<string, string> = { ...fixed };
  const testDataMap =
    step?.dataMap?.testData ??
    step?.dataMap?.["test-data"] ??
    step?.data?.testData ??
    step?.data?.["test-data"] ??
    {};

  for (const [rawKey, rawValue] of Object.entries(testDataMap)) {
    const resolved = valueFromEntry(rawValue);
    if (resolved == null) continue;

    const normalizedKey = normalizeTestDataKey(rawKey);
    const legacyKey = toLegacyTestDataKey(normalizedKey);
    out[normalizedKey] = resolved;
    out[legacyKey] = resolved;
    out[rawKey] = resolved;
  }

  return out;
}

export function resolveAttributeDisplayValue(
  value: unknown,
  fallback = "attribute",
): string {
  if (typeof value === "string") return value || fallback;
  if (value && typeof value === "object") {
    const candidate =
      (value as any).value ??
      (value as any).attributeName ??
      (value as any).actionVariableName;
    if (typeof candidate === "string") return candidate || fallback;
  }
  return fallback;
}
